import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { format as formatTZ } from "date-fns-tz";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertTriangle,
  Plus,
  Menu,
  Info,
  Clock,
  HelpCircle,
} from "lucide-react";
import MenuBar from "@/components/MenuBar";
import CreateEnrollmentDialog from "./PensionsEnrollmentDialog";
import PensionDetails from "./PensionDetails";
import PensionSetupTodos from "./PensionSetupTodos";
import PensionSettings from "./PensionSettings";
import { BankAccountForm } from "./BankAccountForm";
import { PensionSetupConsent } from "./PensionSetupConsent";
import QuickActions from "./QuickActions";
import ApiService from "@/services/api";
import PensionAPIService from "@/services/pension-api";
import { EndpointKeys } from "@/config/api";
import {
  Employee,
  CreateEnrollmentRequest,
  BankAccount,
  PensionConsent,
  PostponementStatus,
  CurrentPensionSelection,
} from "@/types/pensions";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatUTCDateToBritish, parseUTCDate } from "@/lib/dateUtils";
import { useAuth } from "@/hooks/useAuth";
import { isSuperUser } from "@/types/user";
import SuperUserEmployerSelector from "@/components/common/SuperUserEmployerSelector";
import EmployeeSelector from "@/components/common/EmployeeSelector";
import { useEmployees } from "@/contexts/EmployeeContext";
import DashboardContainer from "@/components/layout/DashboardContainer";

export default function PensionsOverview() {
  const { user } = useAuth();
  const { selectedEmployeeId } = useEmployees();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedEmployeeForCreate, setSelectedEmployeeForCreate] = useState<{
    id: number;
    name: string;
    startDate?: Date | null;
    hasActiveEnrollment: boolean;
  } | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>("current");
  const isSuperUserAccount = isSuperUser(user);
  const [hasSelectedEmployer, setHasSelectedEmployer] = useState(false);

  const [bankAccount, setBankAccount] = useState<BankAccount | null>(null);
  const [pensionConsent, setPensionConsent] = useState<PensionConsent | null>(
    null,
  );
  const [quickActionsEmployee, setQuickActionsEmployee] =
    useState<Employee | null>(null);

  // Modal state for TODO widget integration
  const [bankAccountDialogOpen, setBankAccountDialogOpen] = useState(false);
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  const [isSavingBankAccount, setIsSavingBankAccount] = useState(false);
  const [isSavingConsent, setIsSavingConsent] = useState(false);

  // Tab navigation state
  const [activeTab, setActiveTab] = useState<string>("overview");

  // Help popover state
  const [helpPopoverOpen, setHelpPopoverOpen] = useState(false);

  useEffect(() => {
    if (
      !isSuperUserAccount ||
      (isSuperUserAccount && selectedUserId !== "current")
    ) {
      setHasSelectedEmployer(true);
      fetchEmployees();
      fetchPensionSetupData();
    }
  }, [selectedUserId, isSuperUserAccount]);

  // Determine the appropriate setup mode based on actual employee data
  const getSetupMode = () => {
    if (employees.length === 0)
      return { mode: "immediate", title: "Immediate Setup" };

    // Check for active postponements
    const hasActivePostponements = employees.some(
      (employee) =>
        employee.postponementStatus?.isPostponed &&
        !employee.postponementStatus.isExpired,
    );

    // Check for expired postponements (catch-up required)
    const hasExpiredPostponements = employees.some(
      (employee) =>
        employee.postponementStatus?.isPostponed &&
        employee.postponementStatus.isExpired,
    );

    // Check for completed enrollments
    const hasCompletedEnrollments = employees.some(
      (employee) =>
        employee.hasActiveEnrollment &&
        employee.activeEnrollment?.status.toLowerCase() === "completed",
    );

    // Priority order: expired postponements > active postponements > completed enrollments > immediate
    if (hasExpiredPostponements) {
      return {
        mode: "catch-up",
        title: "Catch-up Required",
        description:
          "Some employees have expired postponements and require immediate action.",
      };
    }

    if (hasActivePostponements) {
      return {
        mode: "postponement",
        title: "Postponement Mode",
        description: "Currently managing postponed pension enrollments.",
      };
    }

    if (hasCompletedEnrollments) {
      return {
        mode: "immediate",
        title: "Active Pension Management",
        description: "Managing active pension enrollments and contributions.",
      };
    }

    return {
      mode: "immediate",
      title: "Immediate Setup",
      description: "Set up pension requirements for new enrollments.",
    };
  };

  const setupMode = getSetupMode();

  const fetchEmployees = async () => {
    try {
      setIsLoading(true);
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      const data = await ApiService.request<Employee[]>(
        EndpointKeys.FETCH_PENSIONS_OVERVIEW,
        {
          queryParams,
        },
      );
      const filtered =
        selectedEmployeeId != null
          ? data.filter((e) => e.employeeId === selectedEmployeeId)
          : data;
      setEmployees(filtered);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPensionSetupData = async () => {
    try {
      // Fetch bank account and consent data in parallel
      const [bankAccountData, consentData] = await Promise.all([
        PensionAPIService.getBankAccount().catch(() => null),
        PensionAPIService.getConsent().catch(() => null),
      ]);

      setBankAccount(bankAccountData);
      setPensionConsent(consentData);
    } catch (err) {
      console.error("Error fetching pension setup data:", err);
      // Don't set error state for pension setup data as it's optional
    }
  };

  const createEnrollment = async (
    employeeId: number,
    effectiveDate: Date,
    coverageStartDate: Date,
  ) => {
    try {
      const queryParams: Record<string, string | number> = {};
      if (selectedUserId !== "current") {
        queryParams.userId = selectedUserId;
      }

      await ApiService.request(EndpointKeys.CREATE_ENROLLMENT, {
        method: "POST",
        urlParams: { id: employeeId.toString() },
        queryParams,
        body: {
          effective_date: effectiveDate.toISOString(),
          coverage_start_date: coverageStartDate.toISOString(),
        } as CreateEnrollmentRequest,
      });

      await fetchEmployees();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create enrollment";
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const handleCreateClick = (
    employeeId: number,
    name: string,
    startDate: string | null,
    hasActiveEnrollment: boolean,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();
    setSelectedEmployeeForCreate({
      id: employeeId,
      name,
      startDate: startDate ? new Date(startDate) : null,
      hasActiveEnrollment,
    });
    setCreateDialogOpen(true);
  };

  const getStatusBadgeVariant = (
    status: string,
  ): "default" | "secondary" | "outline" | "destructive" => {
    switch (status.toLowerCase()) {
      case "pending":
        return "secondary";
      case "completed":
        return "default";
      case "executed":
        return "default";
      case "rejected":
        return "destructive";
      default:
        return "outline";
    }
  };

  const getStatusDisplay = (employee: Employee) => {
    // Check postponement status first
    if (employee.postponementStatus?.isPostponed) {
      if (employee.postponementStatus.isExpired) {
        return (
          <Badge
            variant="destructive"
            className="text-sm flex items-center gap-1"
          >
            <AlertTriangle className="h-3 w-3" />
            Catch-up Required
          </Badge>
        );
      }
      return (
        <Badge variant="secondary" className="text-sm flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Postponed until{" "}
          {employee.postponementStatus.postponementEndDate
            ? formatUTCDateToBritish(
                employee.postponementStatus.postponementEndDate,
              )
            : "N/A"}
        </Badge>
      );
    }

    // Default status logic
    if (employee.hasActiveEnrollment) {
      return (
        <Badge
          variant={getStatusBadgeVariant(employee.activeEnrollment!.status)}
          className="text-sm"
        >
          {employee.activeEnrollment!.status}
        </Badge>
      );
    }

    return (
      <Badge variant="outline" className="text-sm">
        No Active Enrollment
      </Badge>
    );
  };

  const handleUserChange = (userId: string) => {
    setSelectedUserId(userId);
    const newUrl = new URL(window.location.href);
    if (userId !== "current") {
      newUrl.searchParams.set("userId", userId);
    } else {
      newUrl.searchParams.delete("userId");
    }
    window.history.replaceState({}, "", newUrl.toString());
  };

  // Modal handlers for TODO widget integration
  const handleBankAccountSubmit = async (data: BankAccount) => {
    setIsSavingBankAccount(true);
    try {
      const savedBankAccount = await PensionAPIService.saveBankAccount(data);
      setBankAccount(savedBankAccount);
      setBankAccountDialogOpen(false);
    } catch (error) {
      console.error("Failed to save bank account:", error);
    } finally {
      setIsSavingBankAccount(false);
    }
  };

  const handleConsentSubmit = async (consentData: PensionConsent) => {
    setIsSavingConsent(true);
    try {
      const savedConsent = await PensionAPIService.recordConsent(consentData);
      setPensionConsent(savedConsent);
      setConsentDialogOpen(false);
    } catch (error) {
      console.error("Failed to save consent:", error);
    } finally {
      setIsSavingConsent(false);
    }
  };

  const HeaderContent = () => {
    if (!isSuperUserAccount) return null;

    return (
      <SuperUserEmployerSelector
        selectedUserId={selectedUserId}
        onUserChange={handleUserChange}
      />
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (selectedEmployee) {
    return (
      <PensionDetails
        employee={selectedEmployee}
        onBack={() => setSelectedEmployee(null)}
        selectedUserId={
          selectedUserId !== "current" ? selectedUserId : undefined
        }
      />
    );
  }

  const TitleWithHelp = (
    <div className="flex items-center space-x-2">
      <h2 className="text-2xl font-bold">Pensions Overview</h2>
      <Popover open={helpPopoverOpen} onOpenChange={setHelpPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700"
          >
            <HelpCircle className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4" align="start">
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Info className="h-4 w-4 text-blue-600" />
              <h4 className="font-semibold text-sm">
                Manage Pension Contributions
              </h4>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              This section allows you to manage the pension configuration for
              your nanny. Hirelo handles the necessary communications and
              filings with The Pensions Regulator and NEST on your behalf. It's
              important to ensure compliance with UK pension regulations.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              If you have any questions about your obligations as an employer or
              need help with the setup, please contact us at{" "}
              <a
                href="mailto:support@hirelo.io"
                className="text-blue-600 hover:underline"
              >
                support@hirelo.io
              </a>
              .
            </p>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );

  return (
    <DashboardContainer title={TitleWithHelp} headerContent={<HeaderContent />}>
      <div className="mb-4">
        <EmployeeSelector
          label="Filter by employee"
          placeholder="All employees"
        />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {(isSuperUserAccount ? hasSelectedEmployer : true) && (
            <div className="mb-4">
              <PensionSetupTodos
                bankAccount={bankAccount}
                consent={pensionConsent}
                onBankAccountClick={() => setBankAccountDialogOpen(true)}
                onConsentClick={() => setConsentDialogOpen(true)}
                showPostponementMode={setupMode.mode === "postponement"}
              />
            </div>
          )}

          {isSuperUserAccount && !hasSelectedEmployer ? (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <div className="mb-4 rounded-full bg-yellow-100 p-4">
                <AlertTriangle className="h-8 w-8 text-yellow-500" />
              </div>
              <h2 className="mb-2 text-xl font-semibold">
                Please Select an Employer
              </h2>
              <p className="max-w-md text-gray-600">
                As a superuser, you need to select an employer from the dropdown
                above before viewing pension information.
              </p>
            </div>
          ) : (
            <ScrollArea className="rounded-md border bg-white">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee Name</TableHead>
                      <TableHead className="hidden md:table-cell">
                        Start Date
                      </TableHead>
                      <TableHead className="hidden lg:table-cell">
                        Employer %
                      </TableHead>
                      <TableHead className="hidden lg:table-cell">
                        Employee %
                      </TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.map((employee) => (
                      <TableRow
                        key={employee.employeeId}
                        data-testid={`employee-row-${employee.employeeId}`}
                        className={
                          employee.hasActiveEnrollment
                            ? "cursor-pointer hover:bg-gray-50"
                            : undefined
                        }
                      >
                        <TableCell
                          data-testid={`employee-name-${employee.employeeId}`}
                        >
                          <div>
                            <div>{`${employee.firstName} ${employee.lastName}`}</div>
                            <div className="md:hidden text-sm text-gray-500">
                              {employee.eligibility.pensionEligibilityDate
                                ? formatUTCDateToBritish(
                                    employee.eligibility.pensionEligibilityDate,
                                  )
                                : "N/A"}
                              {employee.currentSelection &&
                                !employee.currentSelection.isOptedOut && (
                                  <span className="ml-2">
                                    • Employer:{" "}
                                    {
                                      employee.currentSelection
                                        .employerContributionPercent
                                    }
                                    % • Employee:{" "}
                                    {
                                      employee.currentSelection
                                        .employeeContributionPercent
                                    }
                                    %
                                  </span>
                                )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell
                          className="hidden md:table-cell"
                          data-testid={`employee-start-date-${employee.employeeId}`}
                        >
                          {employee.eligibility.pensionEligibilityDate
                            ? formatTZ(
                                parseUTCDate(
                                  employee.eligibility.pensionEligibilityDate,
                                ) || new Date(),
                                "PP",
                                { timeZone: "UTC" },
                              )
                            : "N/A"}
                        </TableCell>
                        <TableCell
                          className="hidden lg:table-cell"
                          data-testid={`employee-employer-percent-${employee.employeeId}`}
                        >
                          {employee.currentSelection &&
                          !employee.currentSelection.isOptedOut
                            ? `${employee.currentSelection.employerContributionPercent}%`
                            : "-"}
                        </TableCell>
                        <TableCell
                          className="hidden lg:table-cell"
                          data-testid={`employee-employee-percent-${employee.employeeId}`}
                        >
                          {employee.currentSelection &&
                          !employee.currentSelection.isOptedOut
                            ? `${employee.currentSelection.employeeContributionPercent}%`
                            : "-"}
                        </TableCell>
                        <TableCell
                          data-testid={`employee-status-${employee.employeeId}`}
                        >
                          {getStatusDisplay(employee)}
                        </TableCell>
                        <TableCell
                          data-testid={`employee-actions-${employee.employeeId}`}
                        >
                          <Button
                            size="sm"
                            variant="default"
                            className="rounded-full whitespace-nowrap"
                            data-testid={`quick-actions-button-${employee.employeeId}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setQuickActionsEmployee(employee);
                            }}
                          >
                            <Menu className="mr-2 h-4 w-4" />
                            <span className="hidden md:inline">
                              Quick Actions
                            </span>
                            <span className="md:hidden">Actions</span>
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </ScrollArea>
          )}
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          {(isSuperUserAccount ? hasSelectedEmployer : true) && (
            <PensionSettings
              bankAccount={bankAccount}
              consent={pensionConsent}
              isLoading={isLoading}
              onBankAccountUpdate={(newBankAccount) =>
                setBankAccount(newBankAccount)
              }
              onConsentUpdate={(newConsent) => setPensionConsent(newConsent)}
              showPostponementMode={setupMode.mode === "postponement"}
            />
          )}
        </TabsContent>
      </Tabs>

      {selectedEmployeeForCreate && (
        <CreateEnrollmentDialog
          open={createDialogOpen}
          onClose={() => {
            setCreateDialogOpen(false);
            setSelectedEmployeeForCreate(null);
          }}
          employeeId={selectedEmployeeForCreate.id}
          employeeName={selectedEmployeeForCreate.name}
          employeeStartDate={selectedEmployeeForCreate.startDate}
          hasExistingEvent={selectedEmployeeForCreate.hasActiveEnrollment}
          onSubmit={createEnrollment}
        />
      )}

      {quickActionsEmployee && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Pension Quick Actions</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setQuickActionsEmployee(null)}
              >
                ✕
              </Button>
            </div>
            <QuickActions
              employee={quickActionsEmployee}
              onActionComplete={() => {
                fetchEmployees(); // Refresh the list
                setQuickActionsEmployee(null); // Close dialog
              }}
            />
          </div>
        </div>
      )}

      {/* Bank Account Dialog */}
      <Dialog
        open={bankAccountDialogOpen}
        onOpenChange={setBankAccountDialogOpen}
      >
        <DialogContent className="w-[95vw] max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {bankAccount ? "Edit Bank Account" : "Set Up Bank Account"}
            </DialogTitle>
          </DialogHeader>
          <BankAccountForm
            initialData={bankAccount || undefined}
            onSubmit={handleBankAccountSubmit}
            isLoading={isSavingBankAccount}
          />
        </DialogContent>
      </Dialog>

      {/* Consent Dialog */}
      <Dialog open={consentDialogOpen} onOpenChange={setConsentDialogOpen}>
        <DialogContent className="w-[95vw] max-w-[600px] md:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {pensionConsent
                ? "Review Consent & Terms"
                : "Pension Setup Consent"}
            </DialogTitle>
          </DialogHeader>
          <PensionSetupConsent
            initialConsent={pensionConsent || undefined}
            termsUrl={PensionAPIService.getPensionTermsPdfUrl()}
            onConsentSubmit={handleConsentSubmit}
            isLoading={isSavingConsent}
          />
        </DialogContent>
      </Dialog>
    </DashboardContainer>
  );
}
